import { useCallback, useMemo, useRef, useState } from 'react';
import { useAtomValue } from 'jotai';
import { Dropdown } from '@carbon/react';
import {
  EAutoRange,
  EAxisAlignment,
  NumberRange,
  NumericAxis,
  NumericLabelProvider,
  Thickness,
} from 'scichart';
import {
  SciChartView,
  type SciChartInitContext,
} from 'components/charts/scichart';
import { mapScopeAtom, serverFiltersAtom } from '../atoms';
import {
  useRegisterChartExport,
  type ChartExportDescriptor,
} from '../chart-export-registry';
import { useEval3Page } from '../context';
import {
  useCalcLifecycle,
  useChartFieldOptions,
  useChartSeries,
} from '../hooks';
import { sampledNote } from './util';
import { ChartStatusOverlay } from './chart-status';
import { ChartLegend } from './chart-legend';
import {
  buildLegend,
  compact,
  familyOf,
  renderChartFamily,
  type RenderAxisConfig,
} from './spec-chart-render';
import type { ChartSeriesEntry, ChartSpec, ChartToggle } from './types';
import styles from './custom-chart.module.scss';

const AXIS_LABEL_STYLE = { fontSize: 11, padding: new Thickness(0, 6, 0, 0) };
const AXIS_TITLE_STYLE = { fontSize: 12 };

/**
 * Generic, config-driven chart body — renders ANY catalog `ChartSpec` (scatter / line /
 * bar / stackedBar) straight from `chart_series`, so a standard chart is a config entry,
 * not a bespoke component. `ChartCard` passes the backend catalog entry's `spec` (and
 * optional `toggle`) here and the chart instantiates itself — no per-chart-type switch case,
 * no hardcoded spec. The actual drawing is delegated to the shared `renderChartFamily`
 * (also used by the Custom builder), so the two renderers never diverge.
 *
 * The optional `toggle` (e.g. Net/Gross) is client-only: all series are fetched once and the
 * selected option filters which render, by series `field` key (no re-request).
 *
 * @param {object} props - The catalog `spec` and optional display `toggle`.
 * @return {JSX.Element} The chart body.
 */
export function SpecChart({
  spec: baseSpec,
  toggle,
  registerExport,
}: {
  spec: ChartSpec;
  toggle?: ChartToggle;
  /** When set, this chart's SciChart surface registers for content export. */
  registerExport?: ChartExportDescriptor;
}) {
  const { calculationId } = useEval3Page();
  const filters = useAtomValue(serverFiltersAtom);
  const mapScope = useAtomValue(mapScopeAtom);
  const { isRunning } = useCalcLifecycle(calculationId);
  // Registers/unregisters this chart's image exporter as its surface mounts/unmounts;
  // a no-op when `registerExport` is absent (chart isn't exportable).
  const onExporterReady = useRegisterChartExport(registerExport);
  // The legend DOM node — passed to the export so it's stacked above the captured chart image
  // (the legend is a sibling of the SciChart canvas, not part of the captured surface).
  const legendRef = useRef<HTMLDivElement>(null);

  // The toggle selection (client-only) — seeded to its default; picks which series render.
  const [toggleOption, setToggleOption] = useState<string>(
    toggle?.default ?? ''
  );

  // The page filters ride inside the spec (chart data matches the table); mapScope is a
  // sibling request field. The spec itself is config — only `filters` is merged in.
  const spec = useMemo<ChartSpec>(
    () => ({ ...baseSpec, ...(filters.length ? { filters } : {}) }),
    [baseSpec, filters]
  );

  const { data, isFetching, error, refetch } = useChartSeries({
    calculationId,
    spec,
    mapScope,
  });

  // The series keys the active toggle option shows (null ⇒ no toggle ⇒ show all).
  const visibleSeriesKeys = useMemo<Set<string> | null>(() => {
    if (!toggle) {
      return null;
    }
    const option =
      toggle.options.find((o) => o.label === toggleOption) ?? toggle.options[0];
    return new Set(option?.series ?? []);
  }, [toggle, toggleOption]);

  // The response series to render — filtered by the toggle when present.
  const seriesData = useMemo<ChartSeriesEntry[]>(
    () =>
      (data?.series ?? []).filter(
        (series) => !visibleSeriesKeys || visibleSeriesKeys.has(series.field)
      ),
    [data, visibleSeriesKeys]
  );

  const family = familyOf(spec);
  const note = sampledNote(data?.meta);
  const hasData = seriesData.length > 0;

  // Field key → human label (from the shared chart_field_options vocabulary, cached across
  // charts) so the legend reads "Net Cash Flow" rather than the raw `netCashflow` series name.
  const { data: fieldOptions } = useChartFieldOptions({ calculationId });
  const labelByField = useMemo(
    () => new Map((fieldOptions?.fields ?? []).map((f) => [f.key, f.label])),
    [fieldOptions]
  );

  // A legend for every chart — one item per rendered series, labelled from the field vocabulary.
  const legend = useMemo(
    () => buildLegend(seriesData, labelByField),
    [seriesData, labelByField]
  );

  // Axis titles from the spec's fields (full label with units, like the Custom chart): the x
  // field, and the y metric when every series shares one field. Omitted for a multi-metric
  // chart (e.g. Production Volumes' oil + gas carry different units, so one y-axis title would
  // be wrong — the legend disambiguates those). Falls back to the raw field id until the field
  // vocabulary loads.
  const axis = useMemo<RenderAxisConfig>(() => {
    const yFields = Array.from(new Set(baseSpec.series.map((s) => s.field)));
    return {
      xTitle: labelByField.get(baseSpec.x.field) ?? baseSpec.x.field,
      yTitle:
        yFields.length === 1
          ? (labelByField.get(yFields[0]) ?? yFields[0])
          : undefined,
    };
  }, [baseSpec, labelByField]);

  const initChart = useCallback(
    ({ surface, wasmContext }: SciChartInitContext) => {
      surface.xAxes.add(
        new NumericAxis(wasmContext, {
          id: 'xAxis',
          axisAlignment: EAxisAlignment.Bottom,
          autoRange: EAutoRange.Always,
          growBy: new NumberRange(0.05, 0.05),
          drawMinorGridLines: false,
          labelStyle: AXIS_LABEL_STYLE,
          axisTitleStyle: AXIS_TITLE_STYLE,
        })
      );
      surface.yAxes.add(
        new NumericAxis(wasmContext, {
          id: 'yAxis',
          axisAlignment: EAxisAlignment.Left,
          autoRange: EAutoRange.Always,
          growBy: new NumberRange(0, 0.1),
          drawMinorGridLines: false,
          labelProvider: Object.assign(new NumericLabelProvider(), {
            formatLabel: compact,
          }),
          labelStyle: AXIS_LABEL_STYLE,
          axisTitleStyle: AXIS_TITLE_STYLE,
        })
      );
    },
    []
  );

  // Delegate the actual drawing to the shared renderer, passing the field-derived axis titles
  // (like the Custom chart). No formatters are overridden — the axes keep the pre-styled
  // `compact` Y formatter and the family-specific X formatter from `renderChartFamily`.
  const renderData = useCallback(
    (ctx: SciChartInitContext) => {
      renderChartFamily(ctx, { family, seriesData, axis });
    },
    [seriesData, family, axis]
  );

  return (
    <div className={styles.body}>
      {toggle && (
        <div className={styles.controls}>
          <Dropdown
            id="spec-chart-toggle"
            titleText={toggle.label}
            label="Select"
            items={toggle.options.map((o) => o.label)}
            itemToString={(item) => item ?? ''}
            selectedItem={toggleOption || toggle.options[0]?.label}
            onChange={({ selectedItem }) =>
              selectedItem && setToggleOption(selectedItem)
            }
            size="md"
          />
        </div>
      )}
      {legend && <ChartLegend ref={legendRef} items={legend} />}
      <div className={styles.chartWrap}>
        <SciChartView
          height="100%"
          initChart={initChart}
          renderData={renderData}
          renderDeps={[seriesData, family, axis]}
          onExporterReady={onExporterReady}
          getExportOverlay={legendRef}
        />
        <ChartStatusOverlay
          isLoading={isFetching || isRunning}
          error={error?.message ?? null}
          isEmpty={!error && !hasData}
          emptyMessage={
            calculationId === null
              ? 'Requires a calculated evaluation.'
              : 'No data for the current selection.'
          }
          note={note}
          onRetry={() => refetch()}
        />
      </div>
    </div>
  );
}
